import { useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useOmniAuth } from '@omni/auth'

// The standard People contract (any Command Centre can provide it; The
// Mortgage Man first, Mortgage-Man-Command-Center 0029_people_admin.sql):
//   public.people_me(), people_list(), people_log(p_limit),
//   people_start_preview(p_email), people_stop_preview(), people_log_open(p_surface)
// The database is the lock: every per-person rule answers for the previewed
// person during "See it as", and the admin calls refuse anyone else.

export interface PeopleMe {
  email: string | null
  is_admin: boolean
  viewing_as: string | null
  viewing_as_name: string | null
  /** null = every tab */
  tabs: string[] | null
}

export interface Person {
  name: string
  email: string
  role: string
  tabs: string[] | null
  mailboxes: string[]
  team_mail: boolean
  web_sign_in: boolean
  claude_login: boolean
  last_sign_in_at: string | null
  last_opened_at: string | null
  removed: boolean
}

export interface AccessEvent {
  at: string
  actor: string | null
  action: string
  target_email: string | null
  detail: Record<string, unknown>
}

function usePublicDb() {
  const { getClient } = useOmniAuth()
  return getClient('crm').schema('public')
}

export function usePeopleMe() {
  const db = usePublicDb()
  return useQuery<PeopleMe | null>({
    queryKey: ['people', 'me'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db.rpc('people_me')
      if (error) throw new Error(error.message)
      return (data as PeopleMe | null) ?? null
    },
  })
}

export function usePeopleList(enabled: boolean) {
  const db = usePublicDb()
  return useQuery<Person[]>({
    queryKey: ['people', 'list'],
    enabled,
    queryFn: async () => {
      const { data, error } = await db.rpc('people_list')
      if (error) throw new Error(error.message)
      return (data ?? []) as Person[]
    },
  })
}

export function usePeopleLog(enabled: boolean) {
  const db = usePublicDb()
  return useQuery<AccessEvent[]>({
    queryKey: ['people', 'log'],
    enabled,
    queryFn: async () => {
      const { data, error } = await db.rpc('people_log', { p_limit: 40 })
      if (error) throw new Error(error.message)
      return (data ?? []) as AccessEvent[]
    },
  })
}

/** Start or stop "See it as". Reloads the app so every page asks again as the right person. */
export function usePreview() {
  const db = usePublicDb()
  const start = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await db.rpc('people_start_preview', { p_email: email })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => window.location.assign('/home'),
  })
  const stop = useMutation({
    mutationFn: async () => {
      const { error } = await db.rpc('people_stop_preview')
      if (error) throw new Error(error.message)
    },
    onSuccess: () => window.location.assign('/people'),
  })
  return { start, stop }
}

/** One row per person, tab and hour (the database skips it during a preview). */
export function useLogOpen() {
  const db = usePublicDb()
  return useCallback(
    (surface: string) => {
      // The request only goes out when awaited; never block the page on it.
      db.rpc('people_log_open', { p_surface: surface }).then(
        () => undefined,
        () => undefined,
      )
    },
    [db],
  )
}

/**
 * Tab visibility for an instance on the People contract: a person's own tab
 * list (null = every tab), and People itself only for an admin who is not
 * previewing. undefined = not known yet. If the question fails, every tab
 * except People shows: the database still refuses what the person may not see.
 */
export function usePeopleVisibility(): (id: string) => boolean | undefined {
  const q = usePeopleMe()
  const me = q.data
  const known = q.isSuccess || q.isError
  return useCallback(
    (id: string) => {
      if (!known) return id === 'home' ? true : undefined
      if (id === 'people') return Boolean(me?.is_admin && !me.viewing_as)
      if (q.isError || !me || me.tabs == null) return true
      return me.tabs.includes(id)
    },
    [known, me, q.isError],
  )
}
